import { cva } from 'class-variance-authority';

/**
 * The button's classes, in a module apart from `button.tsx`.
 *
 * It is not an aesthetic separation: `react-refresh/only-export-components`
 * warns — rightly — when a file exports a component AND something that is not
 * one, because fast refresh stops working for that whole file.
 *
 * It is exported for the one thing a `<button>` cannot be: a LINK that looks
 * like a button. A `<Link>` navigates, opens in another tab on ctrl-click and is
 * announced as a link; a `<button onClick={navigate}>` that looks the same loses
 * all three.
 */
export const buttonVariants = cva(
  // `focus-ring` (defined in styles/theme.css) replaces the previous `ring-2`:
  // the ring over the primary gives 1.91:1, nearly indistinguishable. The
  // utility uses an outline with an offset, so the ring falls on the page's
  // background.
  //
  // Disabled (R7, C-3; supersedes the C-5 roles): never dimmed with opacity,
  // which dropped the primary label to ~2.4:1, and never filled with `bg-subtle`,
  // which resolves to the brand's mist green and read as a pale primary. Every
  // variant swaps to the neutral roles instead: `bg-page` + `text-fg-muted`
  // (text.secondary, ~6.0:1 on it) + `border-border`.
  //
  // A disabled `outline` button still read as an active one in review (the two
  // differ only in the label's grey), so the pointer says it too: pointer
  // events stay ON for `cursor-not-allowed` to show, and every hover/active
  // fill is gated on `not-disabled:` instead, so none of them fires over a
  // disabled button. `not-disabled:` and not `enabled:`: these classes also
  // style `<Link>`s, and an `<a>` never matches `:enabled`.
  // Every variant already draws a 1px border (transparent on `default` and
  // `ghost`), so disabling only changes colours and never shifts the layout.
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-body-s font-medium transition-colors focus-ring disabled:cursor-not-allowed disabled:border-border disabled:bg-page disabled:text-fg-muted',
  {
    variants: {
      variant: {
        default:
          'border border-transparent bg-action text-fg-on-action not-disabled:hover:bg-action-hover not-disabled:active:bg-action-pressed',
        // The hover tints with `action-subtle` and not with a grey: it is an
        // actionable control, and the action's tint says so before the cursor
        // does.
        outline: 'border border-border bg-transparent not-disabled:hover:bg-action-subtle',
        ghost: 'border border-transparent not-disabled:hover:bg-action-subtle',
      },
      // Every size grows to the touch target on a coarse pointer (R4, C-4) and
      // keeps its compact height with a mouse. `min-h-touch` and not a width
      // breakpoint: a tablet with a mouse stays compact, a phone does not.
      //
      // `sm` is 32px (`h-8`) and `min-h-8` pins that as a floor: it is the
      // size of «Ver detalle», «Configurar», «Quitar» in table rows, and a
      // `className` that sets a smaller `h-*` at a call site must not take it
      // under 32px.
      size: {
        default: 'h-9 px-4 py-2 pointer-coarse:min-h-touch',
        sm: 'h-8 min-h-8 px-3 text-caption pointer-coarse:min-h-touch',
        lg: 'h-10 px-6 pointer-coarse:min-h-touch',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

/**
 * The disabled roles for a `Button` disabled with `aria-disabled` instead of
 * the HTML `disabled` attribute: the same `bg-page` + `text-fg-muted` +
 * `border-border` as the base class above (R7, C-3), plus
 * `pointer-events-none`. `aria-disabled` keeps the button focusable and
 * mounted, so a keyboard user does not lose their place while it is
 * unavailable. Shared by `CursorPagination` and the in-flight `DownloadButton`,
 * which used to carry identical copies of this string.
 */
export const ARIA_DISABLED_CLASSNAME =
  'aria-disabled:pointer-events-none aria-disabled:border-border aria-disabled:bg-page aria-disabled:text-fg-muted';
